import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Plus, X, Buildings, Pause, Play, CaretRight, Trash } from "@phosphor-icons/react";
import ConfirmDeleteClubModal from "@/components/ConfirmDeleteClubModal";

export default function SuperClubs() {
  const nav = useNavigate();
  const [clubs, setClubs] = useState(null);
  const [open, setOpen] = useState(false);
  const [deleteClub, setDeleteClub] = useState(null);
  const [form, setForm] = useState({ name: "", country: "UK", timezone: "Europe/London" });
  const [err, setErr] = useState("");

  const refresh = () => api.get("/super/clubs").then((r) => setClubs(r.data)).catch(() => setClubs([]));
  useEffect(() => { refresh(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const res = await api.post("/super/clubs", form);
      setForm({ name: "", country: "UK", timezone: "Europe/London" });
      setOpen(false);
      // Jump straight to the new club so the super admin can import players + invite the head coach
      if (res.data?.club_id) nav(`/super/clubs/${res.data.club_id}`);
      else refresh();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
    }
  };

  const toggleSuspend = async (c) => {
    await api.post(`/super/clubs/${c.id}/suspend`);
    refresh();
  };

  return (
    <div className="space-y-5" data-testid="super-clubs">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="font-label">Platform</div>
          <h1 className="font-heading uppercase text-3xl sm:text-4xl tracking-tight">Clubs</h1>
        </div>
        <button onClick={() => setOpen(true)} className="btn-primary" data-testid="new-club-btn">
          <Plus size={16} weight="bold" /> New club
        </button>
      </div>

      {!clubs && <div className="skeleton h-32" />}

      <div className="grid grid-cols-3 gap-3 mb-2">
        <Stat label="Total clubs" value={clubs?.length} testid="stat-clubs" />
        <Stat label="Active" value={clubs?.filter((c) => c.subscription_status === "active").length} testid="stat-active" />
        <Stat label="Suspended" value={clubs?.filter((c) => c.subscription_status === "suspended").length} testid="stat-suspended" />
      </div>

      <div className="grid gap-2">
        {clubs?.map((c) => (
          <div key={c.id} className="surface p-4" data-testid={`club-row-${c.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex items-start gap-3">
                <div className="w-11 h-11 rounded-md bg-[#1a1a1a] border border-[#27272a] flex items-center justify-center shrink-0">
                  <Buildings size={20} className="text-[#dfff00]" weight="duotone" />
                </div>
                <div>
                  <div className="font-semibold flex items-center gap-2 flex-wrap">
                    {c.name}
                    {c.subscription_status === "suspended" && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-[#ff0026]/10 text-[#ff0026] rounded uppercase tracking-wider font-bold">suspended</span>
                    )}
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    {c.country} • Admin: {c.head_coach?.name || "—"}
                  </div>
                  <div className="text-xs text-zinc-500 mt-1 flex gap-4">
                    <span><span className="text-zinc-300 font-semibold">{c.player_count}</span> players</span>
                    <span><span className="text-zinc-300 font-semibold">{c.coach_count}</span> coaches</span>
                    <span><span className="text-zinc-300 font-semibold">{c.parent_count}</span> parents</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => toggleSuspend(c)} className="btn-ghost" data-testid={`suspend-${c.id}`}>
                  {c.subscription_status === "suspended" ? <Play size={16} weight="fill" /> : <Pause size={16} weight="fill" />}
                </button>
                <button
                  onClick={() => setDeleteClub(c)}
                  className="btn-ghost text-[#ff0026] hover:bg-[#ff0026]/10"
                  data-testid={`delete-${c.id}`}
                  title={`Delete ${c.name}`}
                >
                  <Trash size={16} weight="bold" />
                </button>
                <Link to={`/super/clubs/${c.id}`} className="btn-secondary text-sm" data-testid={`open-${c.id}`}>
                  Drill in <CaretRight size={14} weight="bold" />
                </Link>
              </div>
            </div>
          </div>
        ))}
        {clubs?.length === 0 && <div className="surface p-6 text-zinc-400 text-sm">No clubs yet.</div>}
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/70 z-40 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <form onSubmit={save} className="surface p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="new-club-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading uppercase text-xl">New club</h2>
              <button type="button" onClick={() => setOpen(false)} className="btn-ghost"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="font-label block mb-1">Club name</label>
                <input required className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="club-name" />
              </div>
              <div>
                <label className="font-label block mb-1">Country</label>
                <input required className="input" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
              </div>
              <div className="text-xs text-zinc-500 bg-[#09090b] border border-[#27272a] rounded-md p-3">
                After creating the club you&apos;ll land on its detail page where you can
                bulk-import players and send a head-coach invite via WhatsApp or email.
              </div>
              {err && <div className="text-sm text-[#ff0026]">{err}</div>}
              <button type="submit" className="btn-primary w-full" data-testid="club-submit">Create club</button>
            </div>
          </form>
        </div>
      )}

      <ConfirmDeleteClubModal
        open={!!deleteClub}
        club={deleteClub}
        onClose={() => setDeleteClub(null)}
        onDeleted={() => { setDeleteClub(null); refresh(); }}
      />
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="surface p-4" data-testid={testid}>
      <div className="font-label">{label}</div>
      <div className="font-heading text-4xl text-[#dfff00] mt-1">{value ?? "—"}</div>
    </div>
  );
}
